import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';
import RelatedResources from '../../../components/feature/RelatedResources';

export default function SmallBusinessLoansPage() {
  const loanTypes = [
    {
      title: 'SBA 7(a) Loan Program',
      description: 'The most popular SBA loan program offering up to $5 million for working capital, equipment, and real estate.',
      features: [
        'Loan amounts up to $5 million',
        'Terms up to 25 years for real estate',
        'Competitive interest rates',
        'Can be used for most business purposes'
      ],
      eligibility: 'Small businesses operating for profit in the U.S., meeting SBA size standards'
    },
    {
      title: 'SBA 504 Loan Program',
      description: 'Long-term, fixed-rate financing for major fixed assets like real estate or equipment.',
      features: [
        'Up to $5.5 million for standard projects',
        'Fixed interest rates',
        'Low down payment (10%)',
        'Terms up to 20-25 years'
      ],
      eligibility: 'Businesses purchasing real estate, machinery, or equipment'
    },
    {
      title: 'SBA Microloan Program',
      description: 'Small loans up to $50,000 to help start or grow small businesses.',
      features: [
        'Loans up to $50,000',
        'Average loan size around $13,000',
        'Terms up to 6 years',
        'Technical assistance available'
      ],
      eligibility: 'Startups and small businesses needing smaller amounts'
    }
  ];

  const localLenders = [
    {
      name: 'WestStar Bank',
      description: 'El Paso-based bank offering SBA loans and business lines of credit',
      services: ['SBA 7(a) Loans', 'Commercial Real Estate', 'Equipment Financing', 'Business Lines of Credit']
    },
    {
      name: 'GECU (Government Employees Credit Union)',
      description: 'Local credit union with competitive business loan programs',
      services: ['Business Term Loans', 'Commercial Real Estate', 'Equipment Loans', 'Working Capital']
    },
    {
      name: 'LiftFund',
      description: 'CDFI providing microloans and small business loans in El Paso',
      services: ['Microloans up to $50,000', 'Small Business Loans up to $500,000', 'Business Coaching', 'Technical Assistance']
    },
    {
      name: 'Accion Opportunity Fund',
      description: 'Nonprofit small business lender serving Texas entrepreneurs',
      services: ['Loans $5,000 - $250,000', 'Fast approval process', 'Flexible requirements', 'Business advising']
    }
  ];

  const applicationTips = [
    {
      icon: 'ri-file-text-line',
      title: 'Prepare Your Business Plan',
      description: 'A comprehensive business plan demonstrates your vision and how you\'ll use the loan funds effectively.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Organize Financial Documents',
      description: 'Gather tax returns, bank statements, financial projections, and profit/loss statements for the past 2-3 years.'
    },
    {
      icon: 'ri-user-star-line',
      title: 'Check Your Credit Score',
      description: 'Most lenders require a credit score of 680+. Review your credit report and address any issues beforehand.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Determine Collateral',
      description: 'Identify assets you can use as collateral, such as real estate, equipment, or inventory.'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Calculate Your Needs',
      description: 'Know exactly how much you need and how you\'ll use it. Avoid borrowing more than necessary.'
    },
    {
      icon: 'ri-team-line',
      title: 'Consider a Co-Signer',
      description: 'If your credit or business history is limited, a co-signer can strengthen your application.'
    }
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Small Business Loans",
        "item": "https://elpasotexasbusiness.com/resources/small-business-loans"
      }
    ]
  };

  const relatedResources = [
    {
      title: 'Grant Opportunities',
      description: 'Explore federal, state, and local grant programs that don\'t require repayment.',
      path: '/resources/grant-opportunities',
      icon: 'ri-gift-line'
    },
    {
      title: 'Financial Projections Guide',
      description: 'Create the financial projections lenders require to evaluate your loan application.',
      path: '/resources/financial-projections-guide',
      icon: 'ri-line-chart-line'
    },
    {
      title: 'Business Registration',
      description: 'Complete your business registration before applying for loans and financing.',
      path: '/regulations/business-registration',
      icon: 'ri-file-list-3-line'
    }
  ];

  return (
    <>
      <SEOHead
        title="Small Business Loans in El Paso - SBA Loans & Financing Options"
        description="Find small business loans and financing options in El Paso, Texas. Compare SBA loans, microloans, lines of credit, and local lenders. Learn qualification requirements and application tips."
        canonical="https://elpasotexasbusiness.com/resources/small-business-loans"
        ogImage="https://public.readdy.ai/ai/img_res/ed18af34e328b0b496a8bb1180a74fff.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main id="main-content" className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200 pt-24">
            <div className="max-w-7xl mx-auto px-6 py-6">
              <Link 
                to="/resources" 
                className="text-[#D2691E] hover:text-[#B8571A] font-semibold flex items-center space-x-2 transition-colors whitespace-nowrap"
                aria-label="Return to Resources page"
              >
                <i className="ri-arrow-left-line"></i>
                <span>Back to Resources</span>
              </Link>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Small Business Loans</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Access capital to start or grow your El Paso business through federal, state, and local loan programs. 
                From SBA loans to microloans, find the right financing option for your needs.
              </p>
            </div>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">SBA Loan Programs</h2>
              <div className="space-y-8">
                {loanTypes.map((loan, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-10">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{loan.title}</h3>
                    <p className="text-base text-gray-600 mb-6 leading-relaxed">{loan.description}</p>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-gray-900 mb-4 text-lg">Key Features</h4>
                        <ul className="space-y-3">
                          {loan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-4 text-lg">Eligibility</h4>
                        <p className="text-sm text-gray-700 leading-relaxed">{loan.eligibility}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Local El Paso Lenders</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {localLenders.map((lender, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{lender.name}</h3>
                    <p className="text-sm text-gray-600 mb-6 leading-relaxed">{lender.description}</p>
                    <h4 className="font-bold text-gray-900 mb-3 text-base">Services Offered</h4>
                    <ul className="space-y-2">
                      {lender.services.map((service, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <i className="ri-arrow-right-s-line text-[#D2691E] text-lg mt-0.5"></i>
                          <span className="text-sm text-gray-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Loan Application Tips</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {applicationTips.map((tip, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <i className={`${tip.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{tip.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-20">
              <div className="bg-[#1B3A3A] rounded-3xl p-12">
                <h2 className="text-4xl font-bold text-white mb-6">General Eligibility Requirements</h2>
                <div className="grid md:grid-cols-2 gap-8 text-white/90">
                  <div>
                    <h3 className="font-bold text-xl text-white mb-4">Business Requirements</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Operating for profit in the United States</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Meet SBA size standards for your industry</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Demonstrate ability to repay the loan</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Have a sound business purpose</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-white mb-4">Owner Requirements</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Good personal credit history (typically 680+)</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Invest equity in the business</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Exhaust other financing options first</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <i className="ri-check-line text-[#D2691E] text-xl mt-0.5"></i>
                        <span className="text-sm">Provide personal guarantee</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <div className="bg-[#FAFAF8] rounded-3xl p-12 text-center mb-16">
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Need Help Finding the Right Loan?</h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Our team can help you navigate loan options and connect you with the right lenders for your El Paso business.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg cursor-pointer whitespace-nowrap"
                aria-label="Contact us for loan assistance"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>

          <RelatedResources resources={relatedResources} />
        </main>

        <Footer />
      </div>
    </>
  );
}